import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useService } from '../hooks/useServices';
import { useAudience } from '../contexts/AudienceContext';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { ServiceSchema, BreadcrumbSchema } from '../components/StructuredData';
import { ArrowLeft, CheckCircle2, Lightbulb } from 'lucide-react';
import { useEffect } from 'react';
import { trackCtaClick } from '../lib/analytics';

export function ServiceDetailPage() {
  const { t } = useTranslation(['services', 'audience']);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setAudience } = useAudience();
  const { data: service, isLoading, error } = useService(slug || '');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark pt-16">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-20">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-dark-200 rounded w-48" />
            <div className="h-12 bg-dark-200 rounded w-3/4" />
            <div className="h-6 bg-dark-200 rounded w-full" />
            <div className="h-6 bg-dark-200 rounded w-5/6" />
            <div className="space-y-3 mt-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 bg-dark-200 rounded-xl" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-dark pt-16">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4 font-heading">{t('detail.notFound')}</h1>
          <p className="text-text-secondary mb-8">{t('detail.notFoundDesc')}</p>
          <Link
            to="/"
            className="inline-flex items-center text-neon-cyan hover:text-neon-purple font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('detail.back')}
          </Link>
        </main>
      </div>
    );
  }

  const translatedTitle = t(`services:${service.slug}.title`, { defaultValue: service.name });
  const translatedDesc = t(`services:${service.slug}.description`, { defaultValue: service.description });
  const translatedDetailedInfo = t(`services:${service.slug}.detailed_info`, { defaultValue: service.detailed_info });
  const translatedDeliverables = t(`services:${service.slug}.deliverables`, { returnObjects: true, defaultValue: service.deliverables }) as string[];
  const translatedUseCases = t(`services:${service.slug}.useCases`, { returnObjects: true, defaultValue: service.use_cases }) as string[];

  return (
    <div className="min-h-screen bg-dark">
      <SEO
        title={translatedTitle}
        description={translatedDesc}
        url={`https://www.happytech.dev/services/${slug}`}
        keywords={`${translatedTitle}, บริการ, HappyTech, ${service.audience_types?.join(', ')}`}
      />
      
      <ServiceSchema
        name={translatedTitle}
        description={translatedDesc}
        provider="HappyTech"
        url={`https://www.happytech.dev/services/${slug}`}
        serviceType="Web Development, Mobile Development, Technical Consulting"
      />
      
      <BreadcrumbSchema
        items={[
          { name: 'หน้าหลัก', url: 'https://www.happytech.dev' },
          { name: 'บริการ', url: 'https://www.happytech.dev/#services' },
          { name: translatedTitle, url: `https://www.happytech.dev/services/${slug}` },
        ]}
      />
      
      <Header />

      <main className="pt-16">
        {/* Back button */}
        <div className="border-b border-white/5 bg-dark-100/50 backdrop-blur-sm">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-text-secondary hover:text-neon-cyan transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('detail.back')}
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Header */}
          <div className="mb-10">
            {/* Audience tags */}
            {service.audience_types && service.audience_types.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-text-secondary mr-2">{t('detail.availableFor')}</span>
                {service.audience_types.map((audience) => (
                  <Link
                    key={audience}
                    to={`/audience/${audience}`}
                    onClick={() => setAudience(audience as any)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full bg-neon-purple/10 text-neon-purple border border-neon-purple/20 uppercase tracking-wide hover:bg-neon-cyan/10 hover:text-neon-cyan hover:border-neon-cyan/20 transition-colors cursor-pointer"
                  >
                    {t(`audience:${audience}.title`, { defaultValue: audience })}
                  </Link>
                ))}
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight mb-4 font-heading">
              {translatedTitle}
            </h1>

            <p className="text-lg text-text-secondary leading-relaxed">
              {translatedDesc}
            </p>
          </div>

          {/* Detailed Info */}
          <div className="mb-10">
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <p className="text-text-primary leading-relaxed whitespace-pre-line">
                {translatedDetailedInfo}
              </p>
            </div>
          </div>

          {/* Deliverables */}
          {service.deliverables && service.deliverables.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-neon-cyan" />
                <h2 className="text-xl font-semibold text-text-primary font-heading">{t('detail.whatYouGet')}</h2>
              </div>
              <div className="glass-card rounded-2xl p-6 sm:p-8">
                <ul className="space-y-3">
                  {translatedDeliverables.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-1.5 h-1.5 bg-neon-cyan rounded-full mt-2 shadow-glow-cyan" />
                      <span className="text-text-primary">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Use Cases */}
          {service.use_cases && service.use_cases.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-neon-purple" />
                <h2 className="text-xl font-semibold text-text-primary font-heading">{t('detail.perfectFor')}</h2>
              </div>
              <div className="glass-card rounded-2xl p-6 sm:p-8">
                <ul className="space-y-3">
                  {translatedUseCases.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-1.5 h-1.5 bg-neon-purple rounded-full mt-2" />
                      <span className="text-text-primary">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-gradient-to-r from-neon-cyan to-neon-purple rounded-2xl p-8 sm:p-10 text-center text-white shadow-glow-cyan">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-heading">{t('detail.readyToStart')}</h2>
            <p className="text-white/90 mb-6 max-w-lg mx-auto">
              {t('detail.readyToStartDesc')}
            </p>
            <Link
              to="/contact"
              state={{ serviceId: service.id }}
              onClick={() => trackCtaClick(`service_${slug}`)}
              className="inline-flex items-center justify-center px-6 py-3 bg-dark text-white font-semibold rounded-xl hover:bg-dark-100 transition-colors shadow-lg"
            >
              {t('detail.getInTouch')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
